<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import type { LoanOrderRow } from '@/api/types/domain'
import { fetchLoanOrdersFlat, patchLoanOrderStatus } from '@/api/modules/loanOrders'
import { formatEpoch, formatMoneyInt } from '@/utils/format'

const loading = ref(false)
const list = ref<LoanOrderRow[]>([])

async function load() {
  loading.value = true
  try {
    const { data } = await fetchLoanOrdersFlat()
    list.value = data
  } finally {
    loading.value = false
  }
}

onMounted(load)

const dlg = ref(false)
const editing = ref<LoanOrderRow | null>(null)
const st = ref({ status: 0, statusText: '' })

function openDlg(row: LoanOrderRow) {
  editing.value = row
  st.value.status = row.status ?? 0
  st.value.statusText = row.statusText || ''
  dlg.value = true
}

async function save() {
  if (!editing.value) return
  await patchLoanOrderStatus(editing.value.id, st.value.status, st.value.statusText || undefined)
  ElMessage.success('已更新状态')
  dlg.value = false
  load()
}
</script>

<template>
  <PageChrome
    title="订单快照（全列表）"
    description="AdminLoanOrderController 全列表 + PATCH status。大批量时建议使用「信贷订单」分页页。"
  >
    <template #actions>
      <el-button :loading="loading" type="primary" @click="load">刷新</el-button>
    </template>

    <el-alert type="warning" show-icon :closable="false" style="margin-bottom: 18px">
      该列表为服务端 listAll()；数据量过大时可能影响性能。
    </el-alert>

    <el-card shadow="hover" class="ep-table-scope">
      <el-table v-loading="loading" :data="list" border stripe empty-text="无数据">
        <el-table-column prop="id" label="id" width="76" />
        <el-table-column prop="user" label="用户" width="132" />
        <el-table-column label="本金" align="right">
          <template #default="{ row }">{{ formatMoneyInt((row as LoanOrderRow).money) }}</template>
        </el-table-column>
        <el-table-column prop="months" label="期" width="64" />
        <el-table-column label="月供" align="right">
          <template #default="{ row }">{{ formatMoneyInt((row as LoanOrderRow).monthmoney) }}</template>
        </el-table-column>
        <el-table-column prop="orderNum" label="单号" min-width="148" show-overflow-tooltip />
        <el-table-column prop="status" label="status" width="76" />
        <el-table-column prop="statusText" label="文案" min-width="120" show-overflow-tooltip />
        <el-table-column label="时间" width="168">
          <template #default="{ row }">{{ formatEpoch(row.addtime ?? undefined) }}</template>
        </el-table-column>
        <el-table-column fixed="right" label="" width="120" align="center">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDlg(row as LoanOrderRow)">状态</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <el-dialog v-model="dlg" title="更新订单状态" width="460px" align-center destroy-on-close>
      <el-form label-width="108px">
        <el-form-item label="loan id">{{ editing?.id }}</el-form-item>
        <el-form-item label="status">
          <el-input-number v-model="st.status" controls-position="right" />
        </el-form-item>
        <el-form-item label="statusText"><el-input v-model="st.statusText" /></el-form-item>
      </el-form>
      <template #footer>
        <el-button type="primary" @click="save">保存</el-button>
      </template>
    </el-dialog>
  </PageChrome>
</template>
